import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  LoadingState,
  Modal,
  Select,
  useToast,
} from '@/components/ui';
import { Fact, FactList } from '@/features/admin/Facts';
import { RecordHeader } from '@/features/admin/RecordHeader';
import { InvoiceTable } from '@/features/billing/InvoiceTable';
import { planChangeKind, planChangeTiming, startsNow } from '@/features/billing/planChange';
import { StudentSubscriptionDetail } from '@/features/billing/StudentSubscriptionDetail';
import { UsageMeters } from '@/features/billing/UsageMeters';
import {
  entitlementLines,
  formatMoney,
  formatPrice,
  statusLabel,
  statusTone,
} from '@/features/billing/money';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * One subscription, whole — and the place it is acted on.
 *
 * The actions here are support work: applying a negotiated plan, or ending a
 * subscription for somebody who cannot reach their own screen. They call the
 * same endpoints the company's own billing screen calls, so an upgrade bills
 * now and a downgrade waits for the period already paid for. There is no
 * quieter path for staff, and every change is audited against the admin.
 */
export function AdminSubscriptionDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // Company and student subscriptions share the route; the link says which.
  if (searchParams.get('customer') === 'student') {
    return <StudentSubscriptionDetail key={id} id={id} />;
  }
  return <CompanySubscriptionDetail key={id} id={id} />;
}

function CompanySubscriptionDetail({ id }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [changing, setChanging] = useState(false);
  const [planCode, setPlanCode] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [paying, setPaying] = useState(null);
  const [reference, setReference] = useState('');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-subscription', id],
    queryFn: () => adminApi.getSubscription(id),
  });
  const plans = useQuery({ queryKey: ['admin-plans'], queryFn: adminApi.listBillingPlans });

  function invalidate() {
    [
      'admin-subscription',
      'admin-subscriptions',
      'admin-billing-overview',
      'admin-invoices',
    ].forEach((key) => void queryClient.invalidateQueries({ queryKey: [key] }));
    void queryClient.invalidateQueries({ queryKey: ['admin-company'] });
  }

  const changePlan = useMutation({
    mutationFn: (code) => adminApi.changeSubscriptionPlan(id, code),
    onSuccess: (result) => {
      toast.success(
        result.effective_immediately
          ? 'Plan changed and invoiced'
          : 'Plan change scheduled for the end of the paid period',
      );
      setChanging(false);
      setPlanCode('');
      invalidate();
    },
    onError: (changeError) => toast.error(errorMessage(changeError, 'Could not change the plan')),
  });

  const cancel = useMutation({
    mutationFn: () => adminApi.cancelSubscription(id),
    onSuccess: () => {
      toast.success('Cancellation scheduled');
      setCancelling(false);
      invalidate();
    },
    onError: (cancelError) =>
      toast.error(errorMessage(cancelError, 'Could not cancel the subscription')),
  });

  const resume = useMutation({
    mutationFn: () => adminApi.resumeSubscription(id),
    onSuccess: () => {
      toast.success('Subscription resumed');
      invalidate();
    },
    onError: (resumeError) => toast.error(errorMessage(resumeError, 'Could not resume')),
  });

  const markPaid = useMutation({
    mutationFn: ({ invoiceId, note }) => adminApi.markInvoicePaid(invoiceId, note),
    onSuccess: () => {
      toast.success('Invoice marked paid');
      setPaying(null);
      setReference('');
      invalidate();
    },
    onError: (payError) => toast.error(errorMessage(payError, 'Could not mark it paid')),
  });

  if (isPending) return <LoadingState label="Loading the subscription…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const isEnding = data.cancel_at_period_end || Boolean(data.scheduled_plan);
  const offered = (plans.data ?? []).filter(
    (plan) =>
      plan.audience !== 'STUDENT' &&
      plan.is_active &&
      plan.price_cents > 0 &&
      plan.code !== data.plan_code,
  );
  const chosen = offered.find((plan) => plan.code === planCode);
  // "More expensive" is how the backend decides upgrade from downgrade, so the
  // warning here has to be decided the same way or it will contradict what
  // happens.
  // The same rule as the server: per month against what the company pays now,
  // and never across currencies (a plan in another currency starts now).
  const changeKind = chosen
    ? planChangeKind(chosen, {
        code: data.plan_code,
        price_cents: data.price_cents,
        currency: data.currency,
        interval: data.interval,
        running: data.entitlements_apply,
      })
    : null;
  const isUpgrade = startsNow(changeKind);

  return (
    <div>
      <RecordHeader
        backTo={paths.admin.subscriptions}
        backLabel="Subscriptions"
        eyebrow="Subscription"
        title={data.company_name}
        subtitle={[
          data.plan_name,
          formatPrice(data.price_cents, data.currency, data.interval),
          `since ${formatDate(data.created_at)}`,
        ].join(' · ')}
        badges={
          <>
            <Badge tone={statusTone(data.status)}>{statusLabel(data.status)}</Badge>
            {data.cancel_at_period_end && (
              <Badge tone="warning">Ends {formatDate(data.current_period_end)}</Badge>
            )}
            {data.scheduled_plan && (
              <Badge tone="warning">
                <Icon name="arrowRight" size={13} className="mr-1 inline align-[-2px]" />
                {data.scheduled_plan.name} on {formatDate(data.current_period_end)}
              </Badge>
            )}
            {data.trial_ends_at && (
              <Badge tone="brand">Trial to {formatDate(data.trial_ends_at)}</Badge>
            )}
            {!data.entitlements_apply && <Badge tone="danger">Entitlements lapsed</Badge>}
            {data.company_is_verified ? (
              <Badge tone="success">Verified company</Badge>
            ) : (
              <Badge dot>Unverified company</Badge>
            )}
          </>
        }
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => setChanging(true)}>
              Change plan
            </Button>
            {isEnding ? (
              <Button size="sm" isLoading={resume.isPending} onClick={() => resume.mutate()}>
                Resume
              </Button>
            ) : (
              <Button variant="ghostDanger" size="sm" onClick={() => setCancelling(true)}>
                End subscription
              </Button>
            )}
          </>
        }
        facts={[
          {
            label: 'Price',
            value: formatMoney(data.price_cents, data.currency),
            hint: data.interval === 'YEARLY' ? 'a year' : 'a month',
          },
          {
            label: data.cancel_at_period_end ? 'Ends' : 'Renews',
            value: formatDate(data.current_period_end),
            hint: data.current_period_start
              ? `Period from ${formatDate(data.current_period_start)}`
              : undefined,
          },
          {
            label: 'Outstanding',
            value: formatMoney(data.outstanding_cents, data.currency),
            hint: `${data.open_invoices} open invoice${data.open_invoices === 1 ? '' : 's'}`,
          },
          { label: 'Since', value: formatDate(data.created_at) },
        ]}
      />

      {!data.entitlements_apply && (
        <Alert tone="warning" title="This plan is not being honoured" className="mb-6">
          The paid period has run out and the renewal job has not caught up, so the company is
          currently on free limits. Running renewals from the billing screen bills and extends it.
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader
              title={data.plan_name}
              description={data.plan.description ?? undefined}
              action={
                <span className="text-ink-muted text-sm">
                  {formatPrice(data.price_cents, data.currency, data.interval)}
                </span>
              }
            />
            <ul className="text-ink-muted space-y-1.5 text-sm">
              {entitlementLines(data.plan.entitlements).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            {data.scheduled_plan && (
              <p className="text-ink-muted mt-4 text-sm">
                Moving to <span className="text-ink font-medium">{data.scheduled_plan.name}</span>{' '}
                on {formatDate(data.current_period_end)}. Until then the company keeps everything{' '}
                {data.plan_name} gives it.
              </p>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Usage"
              description="Counted from live rows, not stored — the same numbers the limits are enforced against."
            />
            <UsageMeters
              usage={data.usage}
              entitlements={data.plan.entitlements}
              atLimitNote="At the plan limit. Publishing more is refused until something closes or the plan changes."
            />
          </Card>

          <Card>
            <CardHeader
              title="Invoices"
              description="The last 20. Marking one paid records that money arrived and is audited against your account."
            />
            <InvoiceTable
              invoices={data.invoices}
              emptyTitle="No invoices for this subscription"
              emptyDescription="Invoices appear here once a paid billing period starts."
              action={(invoice) =>
                invoice.status === 'OPEN' ? (
                  <Button
                    variant="ghostPositive"
                    size="xs"
                    title={`Mark invoice ${invoice.number} paid`}
                    onClick={() => setPaying(invoice)}
                  >
                    <Icon name="banknote" size={14} />
                    Mark paid
                  </Button>
                ) : null
              }
            />
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Company" />
            <Link
              to={paths.admin.companyDetail(data.company_id)}
              className="text-ink text-sm font-medium hover:underline"
            >
              {data.company_name}
            </Link>
            <FactList columns={1} className="mt-3">
              <Fact label="Owner" value={data.owner_name} />
              <Fact label="Owner email" value={data.owner_email} />
            </FactList>
            <Link
              to={paths.companyPublic(data.company_slug)}
              className="text-brand-700 mt-3 inline-block text-sm hover:underline"
            >
              Public company page
            </Link>
          </Card>

          <Card>
            <CardHeader title="Schedule" />
            <FactList columns={1}>
              <Fact label="Status" value={statusLabel(data.status)} showEmpty />
              <Fact
                label="Period"
                value={
                  data.current_period_start
                    ? `${formatDate(data.current_period_start)} — ${formatDate(data.current_period_end)}`
                    : formatDate(data.current_period_end)
                }
              />
              <Fact
                label="Trial ends"
                value={data.trial_ends_at ? formatDate(data.trial_ends_at) : null}
              />
              <Fact label="Scheduled plan" value={data.scheduled_plan?.name} />
              <Fact
                label="Cancellation requested"
                value={data.cancelled_at ? formatDate(data.cancelled_at) : null}
              />
              <Fact label="Last change" value={formatDate(data.updated_at)} />
            </FactList>
          </Card>

          <Card>
            <CardHeader title="Payment provider" />
            {data.provider ? (
              <FactList columns={1}>
                <Fact label="Provider" value={data.provider} />
                <Fact label="Customer" value={data.provider_customer_ref} />
                <Fact label="Subscription" value={data.provider_subscription_ref} />
              </FactList>
            ) : (
              /* No vendor is the shipped default, not a misconfiguration: the
                 platform invoices and the company pays by transfer. */
              <EmptyState
                compact
                title="No vendor connected"
                description="This subscription is invoiced by the platform and settled by bank transfer."
              />
            )}
          </Card>
        </div>
      </div>

      <Modal
        open={changing}
        title={`Move ${data.company_name} to another plan`}
        description="This is the company's own change, made on their behalf. It is audited against you."
        onClose={() => setChanging(false)}
      >
        <div className="space-y-4">
          <Select
            label="Plan"
            value={planCode}
            onChange={(event) => setPlanCode(event.target.value)}
          >
            <option value="">Choose a plan…</option>
            {offered.map((plan) => (
              <option key={plan.code} value={plan.code}>
                {plan.name} — {formatPrice(plan.price_cents, plan.currency, plan.interval)}
                {plan.is_public ? '' : ' (private)'}
              </option>
            ))}
          </Select>

          {chosen && (
            <Alert tone={isUpgrade ? 'warning' : 'info'}>
              {isUpgrade
                ? `${planChangeTiming(changeKind)}. ${data.company_name} moves to ${chosen.name} and is invoiced ${formatMoney(chosen.price_cents, chosen.currency)} now.`
                : `${planChangeTiming(changeKind, data.current_period_end)}. ${data.company_name} keeps ${data.plan_name} until then. Nothing already paid for is taken away.`}
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setChanging(false)}>
              Close
            </Button>
            <Button
              disabled={!chosen}
              isLoading={changePlan.isPending}
              onClick={() => changePlan.mutate(planCode)}
            >
              Change plan
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={cancelling}
        title={`End ${data.company_name}'s subscription`}
        message={`They keep ${data.plan_name} until ${formatDate(data.current_period_end)} and fall back to the free plan then. Nothing is lost before that, and this can be undone until it happens.`}
        confirmLabel="Schedule cancellation"
        isLoading={cancel.isPending}
        onConfirm={() => cancel.mutate()}
        onCancel={() => setCancelling(false)}
      />

      <Modal
        open={paying !== null}
        title={`Mark ${paying?.number ?? ''} paid`}
        onClose={() => setPaying(null)}
      >
        <p className="text-ink-muted mb-4 text-sm">
          This records that money arrived. It is audited against your account, so put something here
          that identifies the transfer.
        </p>
        <Field label="Payment reference">
          <Input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder="Bank transfer 2026-08-20, ref INV-2026-000012"
          />
        </Field>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setPaying(null)}>
            Cancel
          </Button>
          <Button
            disabled={reference.trim().length === 0}
            isLoading={markPaid.isPending}
            onClick={() => markPaid.mutate({ invoiceId: paying.id, note: reference.trim() })}
          >
            Mark paid
          </Button>
        </div>
      </Modal>
    </div>
  );
}
